const PENDING_AUTH_RETURN_KEY = "dealhawk_pending_auth_return_v1";
export const PENDING_AUTH_RETURN_TTL_MS = 30 * 60 * 1000;

const PUBLIC_DEALS_QUERY_KEYS = [
  "q",
  "category",
  "status",
  "sort",
  "discount",
  "minPrice",
  "maxPrice",
] as const;

const PUBLIC_DEALS_STATUSES = new Set(["new", "price_drop", "lowest"]);
const PUBLIC_DEALS_SORTS = new Set(["discount", "price_low", "price_high"]);
const PUBLIC_DEALS_DISCOUNTS = new Set(["20", "40", "60", "80"]);
const OPAQUE_ONLINE_DEAL_ID = /^[a-zA-Z0-9._:-]{1,128}$/;

export type GuestDealResumeAction = "save" | "history" | "alert" | "local";

export type GuestDealResumeIntent =
  | { action: "save" | "history"; productId: string }
  | { action: "alert" | "local"; productId?: string };

export interface PendingAuthReturn {
  returnTo: string;
  source?: string;
  createdAt: number;
}

export function sanitizeInternalReturnTo(value?: string | null): string | undefined {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return undefined;
  try {
    const parsed = new URL(value, "https://dealhawk.invalid");
    if (parsed.origin !== "https://dealhawk.invalid") return undefined;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return undefined;
  }
}

export function sanitizeOpaqueOnlineDealId(value?: string | null): string | undefined {
  const candidate = value?.trim();
  return candidate && OPAQUE_ONLINE_DEAL_ID.test(candidate) ? candidate : undefined;
}

function sanitizePublicDealsNumber(value?: string | null): string | undefined {
  if (!value || !/^\d+(?:\.\d{1,2})?$/.test(value)) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1_000_000
    ? value
    : undefined;
}

/**
 * Keeps only public catalog state that the authenticated products page knows
 * how to restore. This prevents auth returns from becoming an arbitrary query
 * string transport while preserving the shopper's visible search and filters.
 */
export function sanitizePublicDealsReturnTo(value?: string | null): string | undefined {
  const safeReturnTo = sanitizeInternalReturnTo(value);
  if (!safeReturnTo) return undefined;

  const parsed = new URL(safeReturnTo, "https://dealhawk.invalid");
  if (!/^\/deals(?:\/[^/?#]+)?\/?$/.test(parsed.pathname)) return undefined;

  const sanitized = new URLSearchParams();
  for (const key of PUBLIC_DEALS_QUERY_KEYS) {
    const raw = parsed.searchParams.get(key);
    if (!raw) continue;
    if (key === "q" || key === "category") {
      const cleaned = raw.replace(/[\r\n\0]/g, "").trim().slice(0, key === "q" ? 120 : 80);
      if (cleaned) sanitized.set(key, cleaned);
    } else if (key === "status" && PUBLIC_DEALS_STATUSES.has(raw)) {
      sanitized.set(key, raw);
    } else if (key === "sort" && PUBLIC_DEALS_SORTS.has(raw)) {
      sanitized.set(key, raw);
    } else if (key === "discount" && PUBLIC_DEALS_DISCOUNTS.has(raw)) {
      sanitized.set(key, raw);
    } else if (key === "minPrice" || key === "maxPrice") {
      const number = sanitizePublicDealsNumber(raw);
      if (number) sanitized.set(key, number);
    }
  }

  const action = parsed.searchParams.get("resume");
  const productId = sanitizeOpaqueOnlineDealId(parsed.searchParams.get("product"));
	if ((action === "save" || action === "history") && productId) {
		sanitized.set("resume", action);
		sanitized.set("product", productId);
	} else if (action === "alert" || action === "local") {
		sanitized.set("resume", action);
		if (productId) sanitized.set("product", productId);
  }

  const query = sanitized.toString();
  return `${parsed.pathname}${query ? `?${query}` : ""}`;
}

export function buildGuestDealAuthReturnTo(
  returnTo: string,
  action?: GuestDealResumeAction,
  productId?: string | null
): string {
  const safeReturnTo = sanitizePublicDealsReturnTo(returnTo) || "/deals";
  const parsed = new URL(safeReturnTo, "https://dealhawk.invalid");
  parsed.searchParams.delete("resume");
  parsed.searchParams.delete("product");

  const safeProductId = sanitizeOpaqueOnlineDealId(productId);
	if ((action === "save" || action === "history") && safeProductId) {
		parsed.searchParams.set("resume", action);
		parsed.searchParams.set("product", safeProductId);
	} else if (action === "alert" || action === "local") {
		parsed.searchParams.set("resume", action);
		if (safeProductId) parsed.searchParams.set("product", safeProductId);
  }

  return `${parsed.pathname}${parsed.search}`;
}

export function getGuestDealResumeIntent(
  search: string | URLSearchParams
): GuestDealResumeIntent | undefined {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  const action = params.get("resume");
  const productId = sanitizeOpaqueOnlineDealId(params.get("product"));
	if ((action === "save" || action === "history") && productId) {
		return { action, productId };
	}
	if (action === "alert" || action === "local") {
		return { action, ...(productId ? { productId } : {}) };
	}
	return undefined;
}

export function getGuestDealUpgradePath(
  intent?: GuestDealResumeIntent,
): string | undefined {
  if (intent?.action !== "alert" && intent?.action !== "local") return undefined;
  return `/settings?tab=billing&upgradeSource=guest_deals_${intent.action}`;
}

export function sanitizeAuthSource(value?: string | null): string | undefined {
  if (!value) return undefined;
  const cleaned = value.toLowerCase().replace(/[^a-z0-9_-]/g, "").slice(0, 64);
  return cleaned || undefined;
}

export function getAuthReturnRetailer(returnTo: string, fallback = "walmart"): string {
  const safeReturnTo = sanitizeInternalReturnTo(returnTo);
  if (!safeReturnTo) return fallback;
  const pathname = new URL(safeReturnTo, "https://dealhawk.invalid").pathname;
  const segments = pathname.split("/").filter(Boolean);
  const candidate = segments[0] === "deals" ? segments[1] : segments[0];
  return sanitizeAuthSource(candidate) || fallback;
}

export function buildGetStartedUrl(returnTo: string, source: string): string {
  const params = new URLSearchParams();
  const safeReturnTo = sanitizeInternalReturnTo(returnTo);
  const safeSource = sanitizeAuthSource(source);
  if (safeReturnTo) params.set("returnTo", safeReturnTo);
  if (safeSource) params.set("source", safeSource);
  const query = params.toString();
  return query ? `/get-started?${query}` : "/get-started";
}

export function persistPendingAuthReturn(returnTo?: string | null, source?: string | null) {
  if (typeof window === "undefined") return;
  const safeReturnTo = sanitizeInternalReturnTo(returnTo);
  if (!safeReturnTo) return;
	const payload: PendingAuthReturn = {
		returnTo: safeReturnTo,
		...(sanitizeAuthSource(source) ? { source: sanitizeAuthSource(source) } : {}),
		createdAt: Date.now(),
  };
  try {
    window.sessionStorage.setItem(PENDING_AUTH_RETURN_KEY, JSON.stringify(payload));
  } catch {
    /* Storage may be unavailable in strict privacy mode. */
  }
}

export function getPendingAuthReturn(): PendingAuthReturn | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.sessionStorage.getItem(PENDING_AUTH_RETURN_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<PendingAuthReturn>;
		const returnTo = sanitizeInternalReturnTo(parsed.returnTo);
		const createdAt = Number(parsed.createdAt);
		const now = Date.now();
		if (
			!returnTo ||
			!Number.isSafeInteger(createdAt) ||
			createdAt <= 0 ||
			createdAt > now ||
			now - createdAt > PENDING_AUTH_RETURN_TTL_MS
		) return undefined;
		return {
			returnTo,
			...(sanitizeAuthSource(parsed.source) ? { source: sanitizeAuthSource(parsed.source) } : {}),
			createdAt,
		};
  } catch {
    return undefined;
  }
}

export function consumePendingAuthReturn(fallback = "/"): string {
  return consumePendingAuthIntent(fallback).returnTo;
}

export function consumePendingAuthIntent(fallback = "/"): PendingAuthReturn {
  const pending = getPendingAuthReturn();
  try {
    window.sessionStorage.removeItem(PENDING_AUTH_RETURN_KEY);
  } catch {
    /* ignore */
  }
	return pending || { returnTo: sanitizeInternalReturnTo(fallback) || "/", createdAt: Date.now() };
}
